import { Button } from '@/components/ui/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/shadcn/dialog';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { useState } from 'react';

interface PhotoGalleryProps {
    photos: string[];
    onDelete?: (index: number) => void;
    canDelete?: boolean;
}

export function PhotoGallery({ photos, onDelete, canDelete = false }: PhotoGalleryProps) {
    const { t } = useTranslations();
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);

    // Helper function to get the full photo URL
    // Photos stored in database are relative paths like "work-orders/photos/filename.jpg"
    // They need to be accessed via /storage/work-orders/photos/filename.jpg
    const getPhotoUrl = (photo: string): string => {
        if (!photo || photo.trim() === '') {
            return '';
        }
        // If photo already starts with http:// or https://, return as is
        if (photo.startsWith('http://') || photo.startsWith('https://')) {
            return photo;
        }
        // If photo already starts with /storage/, return as is
        if (photo.startsWith('/storage/')) {
            return photo;
        }
        // Otherwise, prepend /storage/
        return `/storage/${photo}`;
    };

    // Filter out empty or invalid photos
    const validPhotos = photos.filter((photo) => photo && photo.trim() !== '');

    if (validPhotos.length === 0) {
        return (
            <div className="bg-muted/50 flex flex-col items-center justify-center rounded-lg border py-12 text-center">
                <ImageIcon className="text-muted-foreground mb-3 h-12 w-12" />
                <p className="text-muted-foreground text-sm">{t('work_orders.show.photos.empty', 'No photos uploaded')}</p>
            </div>
        );
    }

    const handleDelete = async (index: number) => {
        if (!onDelete || !canDelete) return;

        if (confirm('Are you sure you want to delete this photo?')) {
            setIsDeleting(index);
            // Simulate API call
            setTimeout(() => {
                onDelete(index);
                setIsDeleting(null);
                if (selectedIndex === index) {
                    setSelectedIndex(null);
                }
            }, 300);
        }
    };

    const nextPhoto = () => {
        if (selectedIndex !== null && selectedIndex < validPhotos.length - 1) {
            setSelectedIndex(selectedIndex + 1);
        }
    };

    const prevPhoto = () => {
        if (selectedIndex !== null && selectedIndex > 0) {
            setSelectedIndex(selectedIndex - 1);
        }
    };

    return (
        <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                {validPhotos.map((photo, index) => (
                    <div
                        key={index}
                        className="group hover:border-primary relative aspect-square cursor-pointer overflow-hidden rounded-lg border transition-colors"
                        onClick={() => setSelectedIndex(index)}
                    >
                        <img
                            src={getPhotoUrl(photo)}
                            alt={`Photo ${index + 1}`}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                                // Hide broken images
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                        {canDelete && onDelete && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(index);
                                }}
                                disabled={isDeleting === index}
                                className={cn(
                                    'bg-destructive text-destructive-foreground absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100',
                                    isDeleting === index && 'opacity-100',
                                )}
                            >
                                {isDeleting === index ? (
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                    <X className="h-4 w-4" />
                                )}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* Full Screen Viewer */}
            <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
                <DialogContent className="max-w-4xl p-0">
                    <DialogHeader className="px-6 pt-6 pb-4">
                        <DialogTitle>
                            Photo {selectedIndex !== null ? selectedIndex + 1 : 0} of {validPhotos.length}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="relative">
                        {selectedIndex !== null && (
                            <>
                                <img
                                    src={getPhotoUrl(validPhotos[selectedIndex])}
                                    alt={`Photo ${selectedIndex + 1}`}
                                    className="h-auto max-h-[70vh] w-full object-contain"
                                    onError={(e) => {
                                        // Show error message for broken images
                                        const img = e.target as HTMLImageElement;
                                        img.style.display = 'none';
                                        const errorDiv = document.createElement('div');
                                        errorDiv.className = 'flex items-center justify-center h-64 text-muted-foreground';
                                        errorDiv.textContent = 'Failed to load image';
                                        img.parentElement?.appendChild(errorDiv);
                                    }}
                                />
                                {selectedIndex > 0 && (
                                    <Button variant="outline" size="icon" className="absolute top-1/2 left-4 -translate-y-1/2" onClick={prevPhoto}>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                )}
                                {selectedIndex < validPhotos.length - 1 && (
                                    <Button variant="outline" size="icon" className="absolute top-1/2 right-4 -translate-y-1/2" onClick={nextPhoto}>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
